import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { ARTICLE_AUDIO_VOICE_LABEL, SMARTASAURUS_TTS_INSTRUCTIONS } from "./audio-voice";

const OPENAI_FALLBACK_VOICE_LABEL = ARTICLE_AUDIO_VOICE_LABEL;
const SIGNED_URL_TTL = 60 * 60 * 24 * 365 * 10;

function prepareFallbackScript(raw: string): string {
  return raw
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/https?:\/\/\S+/gi, " ")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^[-*+]\s+/gm, "")
    .replace(/(\*\*|__)(.*?)\1/g, "$2")
    .replace(/(\*|_)(.*?)\1/g, "$2")
    .replace(/[ \t]+/g, " ")
    .replace(/([.!?])\s+/g, "$1\n\n")
    .slice(0, 4400)
    .trim();
}

async function synthesizeOpenAiFallback(text: string): Promise<Uint8Array> {
  const apiKey = process.env.LOVABLE_API_KEY;
  if (!apiKey) throw new Error("LOVABLE_API_KEY missing");
  const resp = await fetch("https://ai.gateway.lovable.dev/v1/audio/speech", {
    method: "POST",
    headers: { Authorization: `Bearer ${apiKey}`, "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "openai/gpt-4o-mini-tts",
      input: text,
      voice: "ash",
      response_format: "wav",
      // Must stay 1.0 - resampling is the source of S/SH/F/P crackle.
      speed: 1.0,
      instructions: SMARTASAURUS_TTS_INSTRUCTIONS,
    }),
  });

  if (!resp.ok) throw new Error(`fallback TTS ${resp.status}: ${(await resp.text()).slice(0, 300)}`);
  const raw = new Uint8Array(await resp.arrayBuffer());
  if (raw.byteLength < 4000) throw new Error(`fallback TTS returned tiny audio (${raw.byteLength} bytes)`);
  const { polishNarrationWav } = await import("./audio-dsp.server");
  return polishNarrationWav(raw);
}

async function generateFallbackNarration(supabaseAdmin: any, discoveryId: string): Promise<{ status: "ok" | "failed" | "no_text"; audio_url: string | null; error?: string }> {
  const { data: disc } = await supabaseAdmin
    .from("discoveries")
    .select("id,title,excerpt,body")
    .eq("id", discoveryId)
    .maybeSingle();
  if (!disc) return { status: "failed", audio_url: null, error: "discovery not found" };
  const script = prepareFallbackScript([disc.title, disc.excerpt ?? "", disc.body ?? ""].filter(Boolean).join(". "));
  if (!script) return { status: "no_text", audio_url: null, error: "empty script" };
  try {
    const audio = await synthesizeOpenAiFallback(script);
    const path = `${disc.id}/fallback-${Date.now()}.wav`;
    const { error: upErr } = await supabaseAdmin.storage.from("article-audio").upload(path, audio, { contentType: "audio/wav", upsert: false });

    if (upErr) throw new Error(`upload: ${upErr.message}`);
    const { data: signed, error: signErr } = await supabaseAdmin.storage.from("article-audio").createSignedUrl(path, SIGNED_URL_TTL);
    if (signErr || !signed?.signedUrl) throw new Error(`sign: ${signErr?.message ?? "failed"}`);
    const now = new Date().toISOString();
    await supabaseAdmin.from("discoveries").update({
      audio_url: signed.signedUrl,
      audio_generated_at: now,
      audio_checked_at: now,
      audio_bytes: audio.byteLength,
      audio_voice: OPENAI_FALLBACK_VOICE_LABEL,
      audio_error: null,
    }).eq("id", disc.id);
    return { status: "ok", audio_url: signed.signedUrl };
  } catch (e: any) {
    const msg = String(e?.message ?? "fallback failed");
    await supabaseAdmin.from("discoveries").update({ audio_error: msg.slice(0, 500), audio_checked_at: new Date().toISOString() }).eq("id", disc.id);
    return { status: "failed", audio_url: null, error: msg };
  }
}

/**
 * Generate + cache narration for a single discovery. Reusable by the
 * editorial assistant so audio exists BEFORE the reader presses play.
 * Returns the signed audio URL, or null if the discovery has no body.
 */
export async function generateNarrationForDiscovery(
  discoveryId: string,
): Promise<{ status: "ok" | "no_text" | "busy" | "quota" | "failed" | "unavailable"; audio_url: string | null; error?: string }> {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data: disc } = await supabaseAdmin
    .from("discoveries")
    .select("id,title,excerpt,body,audio_url,audio_voice,is_published")
    .eq("id", discoveryId)
    .maybeSingle();
  if (!disc) return { status: "failed", audio_url: null, error: "discovery not found" };
  if (disc.audio_url) return { status: "ok", audio_url: disc.audio_url };
  const { restoreLatestStoredNarration } = await import("./audio-storage.server");
  const restored = await restoreLatestStoredNarration(supabaseAdmin as any, disc.id, { existingVoice: disc.audio_voice });
  if (restored?.audio_url) return { status: "ok", audio_url: restored.audio_url };

  // Production generation uses the locked Smartasaurus narrator path, so admin
  // counts, playback, and generated files cannot disagree on voice.
  const { regenerateNarrationElevenLabs } = await import("./narration-elevenlabs.server");
  const el = await regenerateNarrationElevenLabs(supabaseAdmin as any, discoveryId);
  if (el.status === "ok") return { status: "ok", audio_url: el.audio_url };
  return { status: el.status === "no_text" ? "no_text" : "failed", audio_url: null, error: el.error };

}

export const getArticleAudio = createServerFn({ method: "POST" })
  .inputValidator((d) => z.object({ slug: z.string().min(1).max(200) }).parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: disc, error } = await supabaseAdmin
      .from("discoveries")
      .select("id,audio_url,audio_voice,is_published")
      .eq("slug", data.slug)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!disc || !disc.is_published) throw new Error("Not found");
    if (disc.audio_url) return { audio_url: disc.audio_url, status: "ok" as const };

    const { restoreLatestStoredNarration } = await import("./audio-storage.server");
    const restored = await restoreLatestStoredNarration(supabaseAdmin as any, disc.id, { existingVoice: disc.audio_voice });
    if (restored?.audio_url) return { audio_url: restored.audio_url, status: "ok" as const };

    // Page requests never queue paid narration; missing audio is reported as
    // unavailable so the player shows the article instead of waiting forever.
    return { audio_url: null, status: "pending" as const };
  });

